import type { ContentCardItem } from '@content-search/domain';
import { InlineAudioPlayer } from '@core/components';
import { formatTimestamp, getContentDisplayText } from '@core/utils';
import { ButtonType, Text, IconButton, IconSize, ChevronDown, ChevronUp } from '@soteria/design-system-ve';

import * as contentCardStyles from './ContentCard.css';
import * as styles from './ConversationContextCard.css';

export interface ConversationContextCardProps {
  item: ContentCardItem;
  isSelected?: boolean;
  isOriginalSelection?: boolean;
  alignment: 'left' | 'right';
  onClick?: () => void;
  mediaServiceUrl?: string;
}

// Component for messaging-style content summary with chat-like layout

function MessagingStyleContentSummary({ item, alignment }: { item: ContentCardItem; alignment: 'left' | 'right' }) {
  // Determine if content is safe or harmful for border color and position based on alignment
  const isSafeContent = item.classificationResult !== 'rejected';

  // For left-aligned messages, put the border on the right side
  // For right-aligned messages, put the border on the left side (default)
  let borderClass = '';

  if (alignment === 'left') {
    borderClass = isSafeContent ? contentCardStyles.safeContentBorderRight : contentCardStyles.unsafeContentBorderRight;
  } else {
    borderClass = isSafeContent ? contentCardStyles.safeContentBorder : contentCardStyles.unsafeContentBorder;
  }

  return (
    <div className={`${contentCardStyles.searchContentSummary} ${borderClass}`}>
      {/* Primary Message Content */}
      <div className={contentCardStyles.searchTranscriptionContent}>
        <div className={`${contentCardStyles.textContainer} ${contentCardStyles.contentBoxContainer} ${styles.textContainerNoMargin}`}>
          <div className={`${contentCardStyles.textWrapper} ${styles.textWrapperRelative}`}>
            <Text className={contentCardStyles.searchTranscriptionText}>{getContentDisplayText(item.text, item.contentUrl, item.audioFileUrl)}</Text>
          </div>
        </div>
      </div>

      {/* Labels removed for conversation context - cleaner messaging view */}
    </div>
  );
}

/**
 * ConversationContextCard component for displaying messages in conversation context
 * Shows only the inner content (SearchStyleContentSummary) without the outer Card container
 */
// eslint-disable-next-line complexity
export function ConversationContextCard({
  item,
  isSelected = false,
  isOriginalSelection = false,
  alignment,
  onClick,
  mediaServiceUrl,
}: ConversationContextCardProps) {
  const containerClass = `${styles.contextCardContainer} ${
    alignment === 'left' ? styles.leftAligned : styles.rightAligned
  } ${isSelected ? styles.selectedCard : ''}`;

  const cardClass = `${styles.contextCard} ${alignment === 'left' ? styles.leftCard : styles.rightCard} ${isSelected ? styles.selectedCardInner : ''}`;

  const handleCardClick = () => {
    onClick?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick();
    }
  };

  return (
    <div
      aria-label={`Message from ${(() => {
        const username = item.subjectOwner?.username;
        const userId = item.subjectOwner?.userId;

        if (username && userId) {
          return `${username} (${userId})`;
        } else if (username) {
          return username;
        } else if (userId) {
          return userId;
        } else {
          return 'Unknown User';
        }
      })()}: ${item.text?.substring(0, 100) || getContentDisplayText(undefined, item.contentUrl, item.audioFileUrl)}${item.text && item.text.length > 100 ? '...' : ''}`}
      className={`${styles.messageWrapper} ${isOriginalSelection ? styles.originalSelectedMessage : ''}`}
      data-is-original-selection={isOriginalSelection} // Add data attribute for debugging
      data-message-id={item.id}
    >
      {/* Username and timestamp above message - container matches message bubble width and alignment */}
      <div className={`${styles.metadataContainer} ${alignment === 'right' ? styles.metadataRight : styles.metadataLeft}`}>
        {/* Username and timestamp content */}
        <div className={styles.metadataContent}>
          <span className={styles.username}>
            {(() => {
              const username = item.subjectOwner?.username;
              const userId = item.subjectOwner?.userId;

              if (username && userId) {
                return `${username} (${userId})`;
              } else if (username) {
                return username;
              } else if (userId) {
                return userId;
              } else {
                return 'Unknown User';
              }
            })()}
          </span>
          {item.timestamp && <span className={styles.timestamp}>{formatTimestamp(item.timestamp)}</span>}
        </div>

        {/* Inline Audio Player in the username/timestamp area */}
        {(item.contentUrl || item.audioFileUrl) && (
          <div
            role="button"
            tabIndex={0}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
              }
            }}
          >
            <InlineAudioPlayer audioId={item.id} audioUrl={item.contentUrl || item.audioFileUrl || ''} mediaServiceUrl={mediaServiceUrl} />
          </div>
        )}
      </div>

      {/* Message container with bubble */}
      <div className={containerClass} role="button" tabIndex={0} onClick={handleCardClick} onKeyDown={handleKeyDown}>
        {/* Message bubble arrow */}
        <div className={`${styles.bubbleArrow} ${alignment === 'left' ? styles.leftArrow : styles.rightArrow}`} />

        {/* Only the inner content (MessagingStyleContentSummary) without Card container */}
        <div className={cardClass}>
          <MessagingStyleContentSummary alignment={alignment} item={item} />
        </div>
      </div>
    </div>
  );
}

export interface LoadMoreButtonProps {
  direction: 'up' | 'down';
  onClick: () => void;
  isLoading?: boolean;
}

/**
 * LoadMoreButton component for loading additional conversation context
 */
export function LoadMoreButton({ direction, onClick, isLoading = false }: LoadMoreButtonProps) {
  return (
    <div className={styles.loadMoreContainer}>
      <IconButton
        buttonType={ButtonType.Tertiary}
        disabled={isLoading}
        icon={direction === 'up' ? ChevronUp : ChevronDown}
        iconSize={IconSize.XSmall}
        onClick={onClick}
      />
    </div>
  );
}
